import {
  DisclaimerData,
  getDisclaimersData,
} from "@/pages/quiz/base/questions/disclaimerAcknowledgment/common";
import { ProductConsentGroup } from "@/types/quizFormTypes";

type ProductDisclaimerName =
  | "sermorelinAcknowledgment"
  | "sermorelinPregnancy"
  | "compoundedMedicationDisclosure"
  | "sermorelinMedicationInformation"
  | "telehealthConsent"
  | "adjustmentAcknowledgment"
  | "nadAcknowledgment"
  | "nadPregnancy"
  | "nadCompoundedMedicationDisclosure"
  | "nadMedicationInformation"
  | "nadAdjustmentAcknowledgment";

type Disclaimer = DisclaimerData<ProductDisclaimerName>;

const sermorelinAcknowledgment: Disclaimer = {
  name: "sermorelinAcknowledgment",
  title: "Sermorelin Side Effects Acknowledgment precaution",
  label: "Sermorelin Side Effects Acknowledgment precaution",
  body: (
    <div>
      <p className="mb-2">
        Everyone responds to treatment differently, and some common side effects
        may include:
      </p>
      <ul className="mb-2 list-inside list-disc text-left">
        <li className="ml-4">
          Injection site reactions (redness, swelling, itching, or discomfort)
        </li>
        <li className="ml-4">Flushing or a feeling of warmth</li>
        <li className="ml-4">Headache</li>
        <li className="ml-4">Dizziness</li>
        <li className="ml-4">Nausea</li>
        <li className="ml-4">Joint pain or stiffness</li>
        <li className="ml-4">
          Swelling or puffiness in the hands or feet (fluid retention)
        </li>
        <li className="ml-4">Tingling or numbness in the hands</li>
        <li className="ml-4">Trouble sleeping or unusually vivid dreams</li>
        <li className="ml-4">Drowsiness the following morning</li>
      </ul>
      <p className="mb-2">
        These side effects are usually mild to moderate and often improve over
        time as your body gets used to the medication.
      </p>
      <p className="mb-2">
        While it is normal to experience some mild to moderate side effects as
        your body adjusts, there are certain severe side effects that require
        immediate medical attention. If you experience any of the following,
        please consult your regular in-person healthcare provider immediately:
      </p>
      <ul className="mb-2 list-inside list-disc text-left">
        <li className="ml-4">
          Severe allergic reactions: such as difficulty breathing, swelling of
          the face, lips, or throat, or severe rash
        </li>
        <li className="ml-4">
          Chest pain or pressure, or severe shortness of breath
        </li>
        <li className="ml-4">Severe or persistent headache</li>
        <li className="ml-4">
          Vision changes, such as blurred or double vision
        </li>
        <li className="ml-4">Fainting or near-fainting</li>
        <li className="ml-4">
          Persistent numbness, tingling, or weakness in the hands or arms
        </li>
        <li className="ml-4">A new or growing lump anywhere on the body</li>
        <li className="ml-4">Unexplained weight loss</li>
        <li className="ml-4">
          Signs of high blood sugar, such as excessive thirst, frequent
          urination, or blurred vision
        </li>
      </ul>
      <p className="mb-2">
        By checking the box below, I acknowledge that I have read and understood
        the information provided regarding the potential side effects and risks
        associated with the medication prescribed to me. I agree to follow the
        recommended guidelines and to seek immediate medical attention if I
        experience any severe side effects as outlined.
      </p>
      <p className="mb-4">
        I also understand that it is my responsibility to inform my healthcare
        provider of any unusual or persistent symptoms during my treatment. This
        confirms that I agree to comply with the prescribed treatment plan and
        that I am aware of the necessary precautions to ensure my safety and
        well-being.
      </p>
    </div>
  ),
};

const sermorelinPregnancy: Disclaimer = {
  name: "sermorelinPregnancy",
  title: "Pregnancy and Breastfeeding precaution",
  label: "Pregnancy and Breastfeeding precaution",
  body: (
    <div>
      <p className="mb-2">
        Sermorelin is not prescribed to patients who are pregnant,
        breastfeeding, or actively trying to become pregnant. The effects of
        sermorelin on pregnancy and on a nursing infant are not established.
      </p>
      <p>
        I confirm that I am not currently pregnant, breastfeeding, or actively
        trying to become pregnant. I understand that if any of these change
        during treatment, I must stop taking sermorelin and notify my care team
        immediately so my provider can review my treatment plan.
      </p>
    </div>
  ),
};

const compoundedMedicationDisclosure: Disclaimer = {
  name: "compoundedMedicationDisclosure",
  title: "Compounded Medication Disclosure",
  label: "Compounded Medication Disclosure",
  body: (
    <div>
      <p className="mb-2 font-bold">
        This medication is compounded and is not FDA-approved. FDA has not
        reviewed this compounded medication for safety, effectiveness, or
        quality.
      </p>
      <p className="mb-2">
        Your sermorelin is prepared by a state-licensed compounding pharmacy
        based on your prescription. Compounded medications are not reviewed or
        approved by the FDA, and they are dispensed only after a licensed
        provider determines the treatment is appropriate for you.
      </p>
      <p>I acknowledge that I have read and understood this disclosure.</p>
    </div>
  ),
};

const sermorelinMedicationInformation: Disclaimer = {
  name: "sermorelinMedicationInformation",
  title: "Sermorelin Medication Information",
  label: "Sermorelin Medication Information",
  body: (
    <div>
      <p className="mb-2">
        Sermorelin is a peptide that signals your body to release more of its
        own growth hormone. It is prescribed off-label and is used to support
        lean muscle, sleep quality, and recovery.
      </p>
      <p className="mb-2">
        How it is taken: a small subcutaneous injection at night, five nights on
        and two nights off each week. Your provider sets your starting dose and
        directs any changes over the course of your program.
      </p>
      <p className="mb-2">
        <strong>Sermorelin is not a weight-loss medication.</strong> It does not
        replace or enhance your GLP-1 treatment, and it is not a substitute for
        diet, activity, or the rest of your care plan. Individual results vary.
      </p>
      <p>
        A licensed provider reviews your intake and decides whether sermorelin
        is appropriate for you.{' '}
        <strong>
          A provider may decline to prescribe sermorelin, or refer you to
          in-person care, if they determine it is not appropriate for you.
        </strong>{' '}
        If they decline, you will not be charged.
      </p>
    </div>
  ),
};

const nadAcknowledgment: Disclaimer = {
  name: "nadAcknowledgment",
  title: "NAD+ Side Effects Acknowledgment precaution",
  label: "NAD+ Side Effects Acknowledgment precaution",
  body: (
    <div>
      <p className="mb-2">
        Everyone responds to treatment differently, and some common side effects
        may include:
      </p>
      <ul className="mb-2 list-inside list-disc text-left">
        <li className="ml-4">
          Injection site reactions (redness, swelling, itching, or discomfort)
        </li>
        <li className="ml-4">
          Flushing, warmth, or redness of the face and chest
        </li>
        <li className="ml-4">Nausea or stomach upset</li>
        <li className="ml-4">Headache</li>
        <li className="ml-4">Lightheadedness or dizziness</li>
        <li className="ml-4">
          A feeling of tightness or pressure in the chest during or just after
          the injection
        </li>
        <li className="ml-4">Muscle cramping or tightness</li>
        <li className="ml-4">Fatigue</li>
      </ul>
      <p className="mb-2">
        <strong>Inject slowly.</strong> Injecting NAD+ too quickly is the most
        common cause of flushing, nausea, and chest tightness. Slowing down
        usually prevents or reduces these effects. If they occur, stop, rest,
        and inject more slowly next time.
      </p>
      <p className="mb-2">
        These side effects are usually mild to moderate, are often related to
        how quickly the dose is injected, and commonly improve as your body gets
        used to the medication.
      </p>
      <p className="mb-2">
        While it is normal to experience some mild to moderate side effects as
        your body adjusts, there are certain severe side effects that require
        immediate medical attention. If you experience any of the following,
        please consult your regular in-person healthcare provider immediately:
      </p>
      <ul className="mb-2 list-inside list-disc text-left">
        <li className="ml-4">
          Severe allergic reactions: such as difficulty breathing, swelling of
          the face, lips, or throat, or severe rash
        </li>
        <li className="ml-4">
          Chest pain or pressure that does not resolve after the injection
        </li>
        <li className="ml-4">Severe shortness of breath</li>
        <li className="ml-4">An irregular, racing, or pounding heartbeat</li>
        <li className="ml-4">Fainting or near-fainting</li>
        <li className="ml-4">Severe or persistent headache</li>
        <li className="ml-4">Vision changes</li>
      </ul>
      <p className="mb-2">
        By checking the box below, I acknowledge that I have read and understood
        the information provided regarding the potential side effects and risks
        associated with the medication prescribed to me. I agree to follow the
        recommended guidelines and to seek immediate medical attention if I
        experience any severe side effects as outlined.
      </p>
      <p className="mb-4">
        I also understand that it is my responsibility to inform my healthcare
        provider of any unusual or persistent symptoms during my treatment. This
        confirms that I agree to comply with the prescribed treatment plan and
        that I am aware of the necessary precautions to ensure my safety and
        well-being.
      </p>
    </div>
  ),
};

const nadPregnancy: Disclaimer = {
  name: "nadPregnancy",
  title: "Pregnancy and Breastfeeding precaution",
  label: "Pregnancy and Breastfeeding precaution",
  body: (
    <div>
      <p className="mb-2">
        NAD+ is not prescribed to patients who are pregnant, breastfeeding, or
        actively trying to become pregnant. The effects of NAD+ on pregnancy and
        on a nursing infant are not established.
      </p>
      <p>
        I confirm that I am not currently pregnant, breastfeeding, or actively
        trying to become pregnant. I understand that if any of these change
        during treatment, I must stop taking NAD+ and notify my care team
        immediately so my provider can review my treatment plan.
      </p>
    </div>
  ),
};

const nadCompoundedMedicationDisclosure: Disclaimer = {
  name: "nadCompoundedMedicationDisclosure",
  title: "Compounded Medication Disclosure",
  label: "Compounded Medication Disclosure",
  body: (
    <div>
      <p className="mb-2 font-bold">
        This medication is compounded and is not FDA-approved. FDA has not
        reviewed this compounded medication for safety, effectiveness, or
        quality.
      </p>
      <p className="mb-2">
        Your NAD+ is prepared by a state-licensed compounding pharmacy based on
        your prescription. Compounded medications are not reviewed or approved
        by the FDA, and they are dispensed only after a licensed provider
        determines the treatment is appropriate for you.
      </p>
      <p>I acknowledge that I have read and understood this disclosure.</p>
    </div>
  ),
};

const nadMedicationInformation: Disclaimer = {
  name: "nadMedicationInformation",
  title: "NAD+ Medication Information",
  label: "NAD+ Medication Information",
  body: (
    <div>
      <p className="mb-2">
        NAD+ (nicotinamide adenine dinucleotide) is a coenzyme found in every
        cell. It is involved in the process that converts food into usable
        cellular energy and in enzymes involved in cellular repair. NAD+ is
        prescribed off-label and is used to support daily energy, focus, and
        recovery.
      </p>
      <p className="mb-2">
        How it is taken: a small subcutaneous injection. Your provider sets your
        starting dose and directs how it steps up over your first weeks of
        treatment, and how often you inject thereafter.
      </p>
      <p className="mb-2">
        What the evidence shows: NAD+'s role in cellular energy and repair is
        well established. The clinical evidence for <strong>injectable</strong>{' '}
        NAD+ in people is still emerging — studies are early and ongoing.
        Individual results vary and we cannot promise how you will feel.
      </p>
      <p className="mb-2">
        <strong>NAD+ is not a weight-loss medication.</strong> It does not
        replace or enhance your GLP-1 treatment, and it is not a substitute for
        diet, activity, or the rest of your care plan.
      </p>
      <p className="mb-2">
        <strong>NAD+ is not a treatment for substance use disorder</strong> and
        is not a substitute for addiction treatment or medical detox.
      </p>
      <p>
        A licensed provider reviews your intake and decides whether NAD+ is
        appropriate for you.{' '}
        <strong>
          A provider may decline to prescribe NAD+, or refer you to in-person
          care, if they determine it is not appropriate for you.
        </strong>{' '}
        If they decline, you will not be charged.
      </p>
    </div>
  ),
};

const nadAdjustmentAcknowledgment: Disclaimer = {
  name: "nadAdjustmentAcknowledgment",
  title: "Medication adjustment acknowledgement",
  label: "Medication adjustment acknowledgement",
  body: (
    <div>
      <p className="mb-2">
        I understand that my dose and schedule are set by my prescriber and that{' '}
        <strong>
          I will not change my dose or how often I inject without guidance from
          my provider.
        </strong>
      </p>
      <p>
        I agree to promptly report side effects, any new diagnosis, a pregnancy,
        or changes to the medications and supplements I take, so my provider can
        review my treatment plan.
      </p>
    </div>
  ),
};

const getGlp1Disclaimer = (
  name: "telehealthConsent" | "adjustmentAcknowledgment",
  label: string,
): Disclaimer => ({
  ...getDisclaimersData(true).find((disclaimer) => disclaimer.name === name)!,
  name,
  label,
});

const telehealthConsent = getGlp1Disclaimer(
  "telehealthConsent",
  "Telehealth consent",
);

const adjustmentAcknowledgment = getGlp1Disclaimer(
  "adjustmentAcknowledgment",
  "Medication adjustment acknowledgement",
);

const sermorelinDisclaimers: Disclaimer[] = [
  sermorelinAcknowledgment,
  sermorelinPregnancy,
  telehealthConsent,
  compoundedMedicationDisclosure,
  sermorelinMedicationInformation,
  adjustmentAcknowledgment,
];

const nadDisclaimers: Disclaimer[] = [
  nadAcknowledgment,
  nadPregnancy,
  nadCompoundedMedicationDisclosure,
  nadMedicationInformation,
  nadAdjustmentAcknowledgment,
];

const disclaimersByProductConsentGroup: Record<
  ProductConsentGroup,
  Disclaimer[]
> = {
  sermorelin: sermorelinDisclaimers,
  nad: nadDisclaimers,
};

const pregnancyDisclaimers: ProductDisclaimerName[] = [
  "sermorelinPregnancy",
  "nadPregnancy",
];

export const getDisclaimersDataByProductConsentGroup = (
  group: string,
  isMale: boolean = false,
): Disclaimer[] =>
  (disclaimersByProductConsentGroup[group as ProductConsentGroup] ?? []).filter(
    ({ name }) => !(isMale && pregnancyDisclaimers.includes(name)),
  );
